import { CalendarDays, Loader2, Pencil, Settings2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtDateTime, fmtMoney } from '../../shared/format'
import { useObligationItems, useSaveTaxParams, useTaxParams } from '../api'
import type { TaxParams } from '../types'
import { toastError } from '../ui'
import { Section } from './FinanceBits'
import { Field, FormSection } from './FormBits'

export function TaxParamsCard({ canWrite, regime = 'corporate' }: { canWrite: boolean; regime?: 'corporate' | 'income' }) {
  const { t } = useTranslation()
  const params = useTaxParams()
  const [editing, setEditing] = useState(false)
  const p = params.data
  const income = regime === 'income'
  const rows: [string, string, string?][] = p
    ? [
        [t('finance.tax.params.vatRates'), p.vatRates.map((r) => `%${r}`).join(' / '), t('finance.tax.params.default')],
        [t('finance.tax.params.withholding'), `%${p.withholdingRate}`],
        [t('finance.tax.params.corporate'), income ? t('finance.tax.params.notApplicable') : `%${p.corporateRate}`, t('finance.tax.params.byAccountant')],
        [t('finance.tax.params.incomeBrackets'), income ? t('finance.tax.params.defined', { count: 5 }) : t('finance.tax.params.notApplicable'), t('finance.tax.params.incomeOnly')],
        [t('finance.tax.params.depreciation'), t('finance.tax.params.years', { count: p.depreciationYears })],
        [t('finance.tax.params.exemptions'), t('finance.tax.params.defined', { count: 2 })],
      ]
    : []
  return (
    <Section
      title={t('finance.tax.params.title')}
      icon={Settings2}
      actions={
        canWrite && p ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            <Pencil />
            {t('common.edit')}
          </Button>
        ) : null
      }
    >
      <p className="mb-3 text-xs text-muted-foreground">{t('finance.tax.params.hint')}</p>
      {!p ? (
        <Skeleton className="h-32" />
      ) : (
        <dl className="divide-y">
          {rows.map(([k, v, hint]) => (
            <div key={k} className="flex items-baseline justify-between gap-3 py-2 text-sm">
              <dt className="text-muted-foreground">
                {k}
                {hint ? <span className="block text-[11px]">({hint})</span> : null}
              </dt>
              <dd className="text-right font-medium">{v}</dd>
            </div>
          ))}
          <p className="pt-2 text-[11px] text-muted-foreground">{t('finance.tax.params.updated', { date: fmtDateTime(p.updatedAt) })}</p>
        </dl>
      )}
      {editing && p ? <ParamsForm params={p} onDone={() => setEditing(false)} /> : null}
    </Section>
  )
}

function ParamsForm({ params, onDone }: { params: TaxParams; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveTaxParams()
  const [vat, setVat] = useState(params.vatRates.join(', '))
  const [wh, setWh] = useState(String(params.withholdingRate))
  const [corp, setCorp] = useState(String(params.corporateRate))
  const [dep, setDep] = useState(String(params.depreciationYears))
  const [error, setError] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const rates = vat.split(/[,;\s]+/).filter(Boolean).map(Number)
    const nums = [Number(wh), Number(corp), Number(dep)]
    if (!rates.length || rates.some((r) => !Number.isFinite(r) || r < 0 || r > 100) || nums.some((n) => !Number.isFinite(n) || n < 0 || n > 100)) {
      setError(t('finance.tax.params.invalid'))
      return
    }
    try {
      await save.mutateAsync({ vatRates: rates, withholdingRate: Number(wh), corporateRate: Number(corp), depreciationYears: Number(dep) })
      toast.success(t('common.saved'))
      onDone()
    } catch (err) {
      toastError(err)
    }
  }
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Settings2 />}
      title={t('finance.tax.params.editTitle')}
      description={t('finance.tax.params.hint')}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="tp-vat" label={t('finance.tax.params.vatRates')} hint={t('finance.tax.params.vatHint')}>
          <Input id="tp-vat" value={vat} onChange={(e) => setVat(e.target.value)} />
        </Field>
        <Field id="tp-dep" label={t('finance.tax.params.depreciation')}>
          <Input id="tp-dep" value={dep} onChange={(e) => setDep(e.target.value)} inputMode="numeric" />
        </Field>
        <Field id="tp-wh" label={t('finance.tax.params.withholding')}>
          <Input id="tp-wh" value={wh} onChange={(e) => setWh(e.target.value)} inputMode="decimal" />
        </Field>
        <Field id="tp-corp" label={t('finance.tax.params.corporate')}>
          <Input id="tp-corp" value={corp} onChange={(e) => setCorp(e.target.value)} inputMode="decimal" />
        </Field>
        {error ? <p className="text-xs text-destructive sm:col-span-2">{error}</p> : null}
      </FormSection>
    </Modal>
  )
}

/** Vergi ve SGK vadeleri ödeme planından gelir; ödeme işlemi Ödeme Planı ekranında yapılır. */
export function TaxCalendarCard() {
  const { t } = useTranslation()
  const items = useObligationItems({ kind: 'tax,sgk' })
  const tone = (n: number): BadgeTone => (n <= 3 ? 'danger' : n <= 7 ? 'warning' : 'info')
  const rows = (items.data ?? []).filter((c) => c.status === 'pending' && c.daysLeft <= 60).slice(0, 8)
  return (
    <Section
      title={t('finance.tax.calendar.title')}
      icon={CalendarDays}
      bodyClassName="p-0"
      actions={
        <Button asChild size="sm" variant="ghost">
          <Link to="/finance/obligations">{t('finance.tax.calendar.openPlan')}</Link>
        </Button>
      }
    >
      {items.isLoading ? (
        <Skeleton className="m-4 h-32" />
      ) : rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t('finance.tax.calendar.empty')}</p>
      ) : (
        <ul className="divide-y">
          {rows.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">
                  {c.title} · {c.period}
                </p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {fmtDate(c.dueDate)} · {fmtMoney(c.amount)}
                </p>
              </div>
              <Badge tone={tone(c.daysLeft)} className="whitespace-nowrap">
                {c.daysLeft < 0 ? t('finance.tax.calendar.late', { count: -c.daysLeft }) : t('finance.tax.calendar.daysLeft', { count: c.daysLeft })}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
